<template>
  <BaseModule
    v-bind="$props"
    class="container countdown-module"
    data-module="countdown"
  >
    <div class="content">
      <header
        v-if="currentTitle || currentSubtitle"
        class="base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="currentTitle"
          class="tit tit-g"
        >
          {{ currentTitle }}
        </component>
        <h3
          v-if="currentSubtitle"
          class="sub"
        >
          {{ currentSubtitle }}
        </h3>
      </header>

      <div
        class="timer"
        :style="{ color: timerColor }"
      >
        <div class="time-part">
          <div class="time-part__value">
            {{ remaingTime.days | twoDigits }}
          </div>
          <div class="time-part__label">
            {{ $tc('days', remaingTime.days) }}
          </div>
        </div>
        <div class="time-part">
          <div class="time-part__value">
            {{ remaingTime.hours | twoDigits }}
          </div>
          <div class="time-part__label">
            {{ $tc('hours', remaingTime.hours) }}
          </div>
        </div>
        <div class="time-part">
          <div class="time-part__value">
            {{ remaingTime.minutes | twoDigits }}
          </div>
          <div class="time-part__label">
            {{ $tc('minutes', remaingTime.minutes) }}
          </div>
        </div>
        <div class="time-part">
          <div class="time-part__value">
            {{ remaingTime.seconds | twoDigits }}
          </div>
          <div class="time-part__label">
            {{ $tc('seconds', remaingTime.seconds) }}
          </div>
        </div>
      </div>

      <p class="txt">{{ currentText }}</p>
    </div>
  </BaseModule>
</template>

<script>
import base from '@/mixins/base';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'Countdown',
  filters: {
    twoDigits(number) {
      return number < 10 ? `0${number}` : number;
    },
  },
  components: {
    BaseModule,
  },
  mixins: [base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    text: {
      type: String,
      default: '',
    },
    backgroundColor: {
      type: String,
      default: '#ffffff',
    },
    textColor: {
      type: String,
      default: '#333333',
    },
    timerColor: {
      type: String,
      default: '#333333',
    },
    titleAfterCountdown: {
      type: String,
      default: '',
    },
    subtitleAfterCountdown: {
      type: String,
      default: '',
    },
    textAfterCountdown: {
      type: String,
      default: '',
    },
    date: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      now: new Date(),
      interval: null,
    };
  },
  computed: {
    targetDate() {
      return new Date(this.date);
    },
    timeIsUp() {
      return this.now >= this.targetDate;
    },
    currentTitle() {
      return this.timeIsUp ? this.titleAfterCountdown : this.title;
    },
    currentSubtitle() {
      return this.timeIsUp ? this.subtitleAfterCountdown : this.subtitle;
    },
    currentText() {
      return this.timeIsUp ? this.textAfterCountdown : this.text;
    },
    remaingTime() {
      const remaingTimeInSecs = Math.floor((this.targetDate - this.now) / 1000);

      if (remaingTimeInSecs < 0) {
        return {
          seconds: 0,
          minutes: 0,
          hours: 0,
          days: 0,
        };
      }

      return {
        seconds: remaingTimeInSecs % 60,
        minutes: Math.floor(remaingTimeInSecs / 60) % 60,
        hours: Math.floor(remaingTimeInSecs / 60 / 60) % 24,
        days: Math.floor(remaingTimeInSecs / 60 / 60 / 24),
      };
    },
  },
  watch: {
    date() {
      this.start();
    },
  },
  mounted() {
    this.start();
  },
  methods: {
    start() {
      this.stop();

      if (!this.timeIsUp) {
        this.interval = window.setInterval(() => {
          this.now = new Date();

          if (this.timeIsUp) {
            this.stop();
          }
        }, 1000);
      }
    },
    stop() {
      window.clearInterval(this.interval);
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'date',
            label: 'Data',
            position: 0,
            fields: [
              {
                name: 'date',
                type: 'DatetimePicker',
                label: 'Data final',
                enableTime: true,
                isClearable: false,
              },
            ],
          },
          {
            name: 'text',
            label: 'Conteúdo durante a contagem',
            fields: [
              {
                name: 'text',
                type: 'LongText',
                label: 'Texto de apoio',
              },
            ],
          },
          {
            name: 'afterCount',
            label: 'Conteúdo após a contagem',
            fields: [
              {
                name: 'titleAfterCountdown',
                type: 'ShortText',
                label: 'Título',
              },
              {
                name: 'subtitleAfterCountdown',
                type: 'ShortText',
                label: 'Subtítulo',
              },
              {
                name: 'textAfterCountdown',
                type: 'LongText',
                label: 'Texto de apoio',
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'timerColor',
                after: 'titleColor',
                type: 'ColorPicker',
                label: 'Cor do cronômetro',
              },
            ],
            toRemove: ['buttonColor', 'buttonTextColor'],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      // '0 | 1 | 1+'
      pt: {
        days: 'Dia | Dia | Dias',
        hours: 'Hora | Hora | Horas',
        minutes: 'Minuto | Minuto | Minutos',
        seconds: 'Segundo | Segundo | Segundos',
      },
      en: {
        days: 'Day | Day | Days',
        hours: 'Minute | Minute | Minutes',
        minutes: 'Hour | Hour | Hours',
        seconds: 'Second | Second | Seconds',
      },
      es: {
        days: 'Dia | Dia | Dias',
        hours: 'Hora | Hora | Horas',
        minutes: 'Minuto | Minuto | Minutos',
        seconds: 'Segundo | Segundo | Segundos',
      },
    },
  },
};
</script>

<style scoped lang="scss">
.countdown-module {
  padding: 0;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  .content {
    > :not(:last-child) {
      margin-bottom: 32px;
    }

    .txt {
      line-height: 1.31em;
    }
  }

  .timer {
    display: flex;
    justify-content: center;
    font-size: 100px;
    line-height: 1em;

    .time-part {
      min-width: 2em;
      position: relative;

      &:not(:first-child) {
        margin-left: 20px;

        &::before {
          content: ':';
          position: absolute;
          left: -18px;
        }
      }

      &__value {
        padding-left: 16px; // same as letter-spacing
        margin-bottom: 0.2em;
        font-weight: $lb-medium;
        letter-spacing: 16px;
      }

      &__label {
        font-size: 0.2em;
        line-height: 1em;
        text-transform: uppercase;
        letter-spacing: 0.1em;
      }
    }
  }
}

@include media-breakpoint-down(md) {
  .countdown-module {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);

    .content {
      > :not(:first-child) {
        margin-top: 9.375vw !important;
      }
    }

    .timer {
      font-size: 9.375vw;

      .time-part {
        &:not(:first-child) {
          margin-left: 0.3em;

          &::before {
            left: -0.25em;
          }
        }

        &__value {
          padding-left: 0.2em; // same as letter-spacing
          letter-spacing: 0.2em;
        }
      }
    }
  }
}
</style>
